import { VisibilityState } from '@tanstack/react-table'
import {
  applyFeatureRestrictions,
  createPersistableState,
  orderEquals,
  visibilityEquals,
} from 'components/Table/columnState'

import { DEFAULT_COLUMN_VISIBILITY } from './columns'
import { FRAUD_SCORE_COLUMN_ID } from './columns/fraudScoreColumn'

/**
 * Conditional columns (Fraud Score, defined only once it's enabled) may appear after the column state is
 * initialized, so their stored preferences are kept even when they aren't among the current column ids yet.
 */
const CONDITIONAL_COLUMN_IDS = new Set([FRAUD_SCORE_COLUMN_ID])
const isConditionalColumnId = (id: string) => CONDITIONAL_COLUMN_IDS.has(id)

/**
 * Pure functions for computing column visibility and order state.
 * These are stateless and easily unit testable.
 */

/**
 * Computes the initial column visibility by merging defaults with stored preferences
 * and applying feature flag restrictions.
 */
export function computeInitialVisibility(
  allColumnIds: string[],
  storedVisibility: Record<string, boolean> | undefined,
  disabledColumnIds: Set<string>
): VisibilityState {
  if (!storedVisibility) {
    // No stored preferences — use defaults with feature restrictions applied
    const visibility: VisibilityState = { ...DEFAULT_COLUMN_VISIBILITY }
    disabledColumnIds.forEach((id) => {
      visibility[id] = false
    })
    return visibility
  }

  // Restore from stored, falling back to false for unknown columns
  const visibility: VisibilityState = {}
  for (const [id, isVisible] of Object.entries(storedVisibility)) {
    if (isConditionalColumnId(id)) {
      visibility[id] = isVisible
    }
  }
  for (const id of allColumnIds) {
    visibility[id] = storedVisibility[id] ?? false
  }

  // Force-hide columns disabled by feature flags
  disabledColumnIds.forEach((id) => {
    visibility[id] = false
  })

  return visibility
}

/**
 * Computes the initial column order by using stored order if available,
 * appending any new columns that weren't in the stored order.
 */
export function computeInitialOrder(allColumnIds: string[], storedOrder: string[] | undefined): string[] {
  if (!storedOrder || storedOrder.length === 0) {
    return allColumnIds
  }

  // Start with stored order, then append any new columns not in the stored order
  const storedSet = new Set(storedOrder)
  const newColumns = allColumnIds.filter((id) => !storedSet.has(id))

  // Also filter out any columns that no longer exist
  const allColumnSet = new Set(allColumnIds)
  const validStoredOrder = storedOrder.filter((id) => allColumnSet.has(id) || isConditionalColumnId(id))

  return [...validStoredOrder, ...newColumns]
}

/**
 * Checks if the column order needs new columns appended.
 * Returns the updated order if changes are needed, or null if no changes.
 */
export function reconcileColumnOrder(currentOrder: string[], allColumnIds: string[]): string[] | null {
  if (currentOrder.length === 0) {
    return null // Not initialized yet
  }

  const currentSet = new Set(currentOrder)
  const missingColumns = allColumnIds.filter((id) => !currentSet.has(id))

  if (missingColumns.length === 0) {
    return null // No changes needed
  }

  // Conditional columns appear after initialization, so place them where they're defined (e.g. Fraud Score
  // after Suspect Score) rather than at the end.
  const alwaysDefinedColumns = missingColumns.filter((id) => !isConditionalColumnId(id))
  const conditionalColumns = missingColumns.filter(isConditionalColumnId)

  return insertAfterPredecessors([...currentOrder, ...alwaysDefinedColumns], conditionalColumns, allColumnIds)
}

/**
 * Keeps stored conditional columns that aren't defined right now (e.g. Fraud Score while the flag loads)
 * when the user sets a new order, so their position isn't lost. The column selector only
 * knows the loaded columns.
 */
export function keepUnloadedColumns(previousOrder: string[], nextOrder: string[], allColumnIds: string[]): string[] {
  const next = new Set(nextOrder)
  const loaded = new Set(allColumnIds)
  const unloaded = previousOrder.filter((id) => isConditionalColumnId(id) && !next.has(id) && !loaded.has(id))

  return unloaded.length === 0 ? nextOrder : insertAfterPredecessors(nextOrder, unloaded, previousOrder)
}

/**
 * Inserts each of `ids` right after its nearest preceding id in `reference` that is already in `order`,
 * or first if there is none. `ids` must follow their order in `reference`.
 */
function insertAfterPredecessors(order: string[], ids: string[], reference: string[]): string[] {
  const result = [...order]

  for (const id of ids) {
    let insertAt = 0
    for (let i = reference.indexOf(id) - 1; i >= 0; i--) {
      const position = result.indexOf(reference[i])
      if (position !== -1) {
        insertAt = position + 1
        break
      }
    }
    result.splice(insertAt, 0, id)
  }

  return result
}

/**
 * Hides columns that have no visibility entry yet. TanStack Table treats a missing entry as visible, but
 * conditional columns (e.g. Fraud Score) can appear after the state was initialized and must stay hidden
 * until the user opts in. Returns the same object when nothing is missing.
 */
export function hideColumnsWithoutVisibility(visibility: VisibilityState, allColumnIds: string[]): VisibilityState {
  const missing = allColumnIds.filter((id) => visibility[id] === undefined)

  if (missing.length === 0) {
    return visibility
  }

  const result = { ...visibility }
  for (const id of missing) {
    result[id] = false
  }
  return result
}

export { applyFeatureRestrictions, createPersistableState, orderEquals, visibilityEquals }
